import { useDroppable } from "@dnd-kit/core";
import * as React from "react";

import { Badge, Button, Spinner } from "@/components/ui";
import { cn } from "@/lib/utils";
import { User, VideoProject } from "@/services/bedrock";

import { KanbanCard } from "./KanbanCard";
import { KanbanColumnDef, KanbanColumnId } from "./kanbanColumns";
export const KANBAN_DROP_PLACEHOLDER_ATTR = "data-kanban-drop-placeholder";

export type KanbanColumnDropState = "idle" | "valid" | "valid-hover" | "invalid" | "source";

const EMPTY_PROJECTS: VideoProject[] = [];

interface KanbanColumnProps {
  column: KanbanColumnDef;
  projects: VideoProject[];
  totalCount: number;
  loading?: boolean;
  disabled?: boolean;
  pendingProjectId?: number | null;
  isDragging?: boolean;
  activeDragId?: string | null;
  activeProjectId?: string | null;
  collapseActiveCard?: boolean;
  dropPlaceholderIndex?: number | null;
  dropPlaceholderHeight?: number;
  dropState?: KanbanColumnDropState;
  hasMore?: boolean;
  loadingMore?: boolean;
  isCardDraggable: (project: VideoProject) => boolean;
  focusedProjectId?: number | null;
  staffUsers: User[];
  currentUserId?: number;
  onAssigneeChange?: (project: VideoProject, userId: number | null) => void;
  onQaChange?: (project: VideoProject, userId: number | null) => void;
  onStatusChange?: (project: VideoProject, columnId: KanbanColumnId) => void;
  onLoadMore?: (columnId: KanbanColumnId) => void;
}

function KanbanDropPlaceholder({ height }: { height?: number }) {
  return (
    <div
      aria-hidden="true"
      data-kanban-drop-placeholder=""
      className="rounded-lg bg-emerald-50/70 ring-2 ring-dashed ring-emerald-400/80"
      style={height != null ? { height } : { minHeight: 72 }}
    />
  );
}

export const KanbanColumn = React.memo(function KanbanColumn({
  column,
  projects,
  totalCount,
  loading = false,
  disabled = false,
  pendingProjectId = null,
  isDragging = false,
  activeDragId = null,
  activeProjectId = null,
  collapseActiveCard = false,
  dropPlaceholderIndex = null,
  dropPlaceholderHeight,
  dropState = "idle",
  hasMore = false,
  loadingMore = false,
  isCardDraggable,
  focusedProjectId = null,
  staffUsers,
  currentUserId,
  onAssigneeChange,
  onQaChange,
  onStatusChange,
  onLoadMore,
}: KanbanColumnProps) {
  const { setNodeRef } = useDroppable({
    id: column.id,
    disabled: isDragging && dropState === "invalid",
  });

  const cards = (projects.length ? projects : EMPTY_PROJECTS).map(project => (
    <KanbanCard
      key={project.id}
      videoProject={project}
      staffUsers={staffUsers}
      currentUserId={currentUserId}
      disabled={disabled || pendingProjectId === project.id}
      draggable={isCardDraggable(project) && pendingProjectId !== project.id}
      collapseInPlace={collapseActiveCard && activeProjectId === String(project.id)}
      isDragSource={activeDragId === String(project.id)}
      selected={focusedProjectId === project.id}
      onAssigneeChange={onAssigneeChange}
      onQaChange={onQaChange}
      onStatusChange={onStatusChange}
    />
  ));

  if (dropPlaceholderIndex != null) {
    cards.splice(
      Math.min(dropPlaceholderIndex, cards.length),
      0,
      <KanbanDropPlaceholder key="kanban-drop-placeholder" height={dropPlaceholderHeight} />
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col min-w-60 flex-1 transition-opacity duration-200",
        dropState === "invalid" && "opacity-45"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between mb-3 px-0.5 transition-colors",
          dropState === "invalid" && "text-slate-400"
        )}
      >
        <h3 className="text-xs font-semibold leading-tight">{column.label}</h3>
        <Badge
          variant="outline"
          size="small"
          className={cn("bg-white border-slate-200", dropState === "invalid" ? "text-slate-400" : "text-slate-600")}
        >
          {loading ? "..." : totalCount}
        </Badge>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          "flex flex-col gap-2 min-h-[200px] flex-1 rounded-lg border border-dashed p-2 transition-[border-color,background-color,box-shadow] duration-200",
          dropState === "idle" && "border-slate-200 bg-slate-50/40",
          dropState === "source" && "border-slate-300 bg-slate-100/50",
          dropState === "invalid" && "border-slate-200 bg-slate-100/80 cursor-not-allowed",
          dropState === "valid" && "border-emerald-400 bg-emerald-50/40 ring-1 ring-emerald-200/60",
          dropState === "valid-hover" && "border-emerald-500 bg-emerald-50/70 ring-2 ring-emerald-300/70"
        )}
      >
        {loading ? "Loading..." : cards}
        {!loading && projects.length === 0 && !isDragging && (
          <div className="text-[11px] text-slate-400 text-center py-4 px-2">No projects</div>
        )}
        {!loading && hasMore && !isDragging && (
          <Button
            variant="ghost"
            size="xs"
            className="w-full text-[11px] text-slate-600 hover:text-slate-900"
            disabled={loadingMore || disabled}
            onClick={() => onLoadMore?.(column.id)}
          >
            {loadingMore ? (
              <>
                <Spinner className="size-3" />
                Loading...
              </>
            ) : (
              "Load More"
            )}
          </Button>
        )}
      </div>
    </div>
  );
});
